import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Minus, Plus, Shield, Package, Lock, Moon, Zap, Heart, FileText, ExternalLink } from 'lucide-react';
import { getProductById, getFeaturedProducts, SIZE_OPTIONS } from '../data/products';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('3.5g');
  const [isAdding, setIsAdding] = useState(false);

  const product = getProductById(id);
  const relatedProducts = getFeaturedProducts().filter(p => p.id !== id).slice(0, 2);

  // Calculate price based on selected size
  const currentPrice = useMemo(() => {
    if (!product) return 0;
    const sizeOption = SIZE_OPTIONS.find(s => s.id === selectedSize);
    return Math.round(product.basePrice * (sizeOption?.multiplier || 1));
  }, [product, selectedSize]);

  if (!product) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-zinc-50 mb-4">Secret Not Found</h1>
          <button
            onClick={() => navigate('/collection')}
            className="text-zinc-400 hover:text-white font-sans text-sm transition-colors"
          >
            Return to Collection
          </button>
        </div>
      </div>
    );
  }

  const handleAddToCollection = () => {
    setIsAdding(true);
    addItem({ ...product, price: currentPrice, selectedSize }, quantity);
    setTimeout(() => {
      setIsAdding(false);
      navigate('/checkout');
    }, 600);
  };

  const handleViewReport = () => {
    // Open placeholder PDF in new tab
    window.open(product.reportUrl || '#', '_blank');
  };

  // Get category icon
  const getCategoryIcon = () => {
    switch (product.category) {
      case 'night': return Moon;
      case 'focus': return Zap;
      case 'calm': return Heart;
      default: return Shield;
    }
  };
  const CategoryIcon = getCategoryIcon();

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Back Button */}
      <div className="px-6 md:px-12 lg:px-24 pt-24">
        <button
          data-testid="back-to-collection"
          onClick={() => navigate('/collection')}
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white font-sans text-xs tracking-widest uppercase transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Collection
        </button>
      </div>

      {/* Product Content */}
      <section className="py-12 md:py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Product Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-[3/4] bg-[#0A0A0A] border border-white/5 overflow-hidden">
                <img
                  data-testid="product-image"
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Limited Availability Badge */}
              {product.inStock && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="absolute bottom-6 left-6"
                >
                  <div className="flex items-center gap-2 bg-[#050505]/80 backdrop-blur-sm px-4 py-2 border border-white/10">
                    <div className="w-1.5 h-1.5 bg-zinc-500 rounded-full" />
                    <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-zinc-400">
                      Limited availability
                    </span>
                  </div>
                </motion.div>
              )}
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-col justify-center"
            >
              {/* Category Badge */}
              <div className="flex items-center gap-2 mb-6">
                <CategoryIcon className="w-4 h-4 text-zinc-600" />
                <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-zinc-600">
                  {product.category}
                </p>
              </div>

              {/* Title */}
              <h1 
                data-testid="product-name"
                className="font-serif text-4xl sm:text-5xl font-light text-zinc-50 mb-2"
              >
                {product.name}
              </h1>
              
              <p className="font-serif text-lg text-zinc-400 italic mb-8">
                — {product.secretName}
              </p>

              {/* Emotional Description */}
              <div 
                data-testid="product-description"
                className="mb-8"
              >
                {product.longDescription.split('\n\n').map((paragraph, idx) => (
                  <p 
                    key={idx}
                    className={`font-sans leading-relaxed mb-3 last:mb-0 ${
                      idx === 0 
                        ? 'text-base text-zinc-300 italic' 
                        : 'text-sm text-zinc-500'
                    }`}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              {/* Effect Profile */}
              {product.effectProfile && (
                <div className="mb-8">
                  <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-zinc-600 mb-2">
                    Effect Profile
                  </p>
                  <p className="font-sans text-sm text-zinc-300 tracking-wide">
                    {product.effectProfile}
                  </p>
                </div>
              )}

              {/* Experience Blocks */}
              {product.experience && (
                <div className="mb-8 p-6 bg-zinc-900/20 border border-white/5">
                  <div className="grid grid-cols-1 gap-5">
                    <div>
                      <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-zinc-600 mb-1">
                        Experience
                      </p>
                      <p className="font-sans text-sm text-zinc-400">
                        {product.experience.feeling}
                      </p>
                    </div>
                    
                    <div className="w-full h-px bg-white/5" />
                    
                    <div>
                      <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-zinc-600 mb-1">
                        Best For
                      </p>
                      <p className="font-sans text-sm text-zinc-400">
                        {product.experience.bestFor}
                      </p>
                    </div>
                    
                    <div className="w-full h-px bg-white/5" />
                    
                    <div>
                      <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-zinc-600 mb-1">
                        Atmosphere
                      </p>
                      <p className="font-sans text-sm text-zinc-400">
                        {product.experience.atmosphere}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Size Selection */}
              <div className="mb-8">
                <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-4">
                  Choose your format
                </p>
                <div className="flex gap-3">
                  {SIZE_OPTIONS.map((size) => (
                    <button
                      key={size.id}
                      data-testid={`size-${size.id}`}
                      onClick={() => setSelectedSize(size.id)}
                      className={`px-6 py-3.5 font-sans text-xs tracking-wide transition-all duration-300 ${
                        selectedSize === size.id
                          ? 'bg-white text-black shadow-[0_0_20px_rgba(255,255,255,0.1)]'
                          : 'border border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200'
                      }`}
                    >
                      {size.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div className="mb-8">
                <p 
                  data-testid="product-price"
                  className="font-serif text-3xl text-zinc-50"
                >
                  ${currentPrice} <span className="text-lg text-zinc-500">— {selectedSize}</span>
                </p>
              </div>

              {/* Quantity & Add to Collection */}
              <div className="flex items-center gap-4 mb-4">
                {/* Quantity Selector */}
                <div className="flex items-center border border-zinc-800">
                  <button
                    data-testid="quantity-decrease"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/5 transition-all"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span 
                    data-testid="quantity-value"
                    className="w-12 h-12 flex items-center justify-center font-sans text-white"
                  >
                    {quantity}
                  </span>
                  <button
                    data-testid="quantity-increase"
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/5 transition-all"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {/* Add to Collection Button */}
                <button
                  data-testid="add-to-collection-button"
                  onClick={handleAddToCollection}
                  disabled={!product.inStock || isAdding}
                  className="flex-1 bg-white text-black py-4 font-sans text-xs font-medium tracking-[0.2em] uppercase hover:bg-zinc-100 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isAdding ? (
                    <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                  ) : product.inStock ? (
                    'Add to Collection'
                  ) : (
                    'Currently Unavailable'
                  )}
                </button>
              </div>

              {/* Members Only Text */}
              <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-zinc-600 mb-10 text-center">
                Members only access
              </p>

              {/* Trust Layer */}
              <div className="py-6 border-t border-white/10 mb-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3 text-zinc-400">
                    <Shield className="w-4 h-4" />
                    <span className="font-sans text-sm tracking-wide">Lab-tested</span>
                  </div>
                  <button
                    data-testid="view-report-button"
                    onClick={handleViewReport}
                    className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="font-sans text-xs tracking-wide">View Report</span>
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
                
                {/* Additional Trust Badges */}
                <div className="grid grid-cols-2 gap-5 pt-6 border-t border-white/5">
                  <div className="flex items-center gap-3 text-zinc-500">
                    <Package className="w-4 h-4" />
                    <span className="font-sans text-xs tracking-wide">Discreet Packaging</span>
                  </div>
                  <div className="flex items-center gap-3 text-zinc-500">
                    <Lock className="w-4 h-4" />
                    <span className="font-sans text-xs tracking-wide">Secure Checkout</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Secrets */}
      {relatedProducts.length > 0 && (
        <section className="py-24 px-6 md:px-12 lg:px-24 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-zinc-600 mb-3 text-center">
              Continue Exploring
            </p>
            <h2 className="font-serif text-2xl text-zinc-50 mb-12 text-center">
              Other Secrets
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-2xl mx-auto">
              {relatedProducts.map((relatedProduct) => (
                <div
                  key={relatedProduct.id}
                  data-testid={`related-product-${relatedProduct.id}`}
                  onClick={() => {
                    navigate(`/product/${relatedProduct.id}`);
                    window.scrollTo(0, 0);
                  }}
                  className="group cursor-pointer"
                >
                  <div className="aspect-[3/4] bg-[#0A0A0A] border border-white/5 overflow-hidden mb-4 group-hover:border-white/20 transition-all duration-500">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-zinc-600 mb-1">
                    {relatedProduct.secretName}
                  </p>
                  <h3 className="font-serif text-lg text-zinc-50 mb-1 group-hover:text-white transition-colors">
                    {relatedProduct.name}
                  </h3>
                  <p className="font-sans text-xs text-zinc-600 italic">
                    {relatedProduct.tagline}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">
            © 2025 Secret 13. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ProductPage;
